import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Flag,
  Calendar,
  Clock,
  Timer,
  User,
  CircleDot,
  Hash,
  Layers,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { TaskDatePicker } from '../TaskListView/TaskDatePicker';
import { MetadataRow } from './MetadataRow';
import { useToast } from '@/shared/hooks/useToast';
import { useUIStore } from '@/core/stores/ui.store';
import { PriorityPicker } from '../TaskListView/PriorityPicker';
import { GroupPicker } from '../GroupPicker';
import { TaskTimerControl } from './TaskTimerControl';
import { TagPicker } from '../TagPicker';
import { StatusPicker } from './StatusPicker';
import { AssigneePicker } from './AssigneePicker';
import { EstimatedPicker } from '../EstimatedPicker';
import { useProjectFields, FieldValueCell } from '@/features/custom-fields/public';
import type { TaskDetail, UpdateTaskDetailPayload } from '../../types';
import type { Tag } from '@/features/tags/public';

interface TaskMetadataGridProps {
  task: TaskDetail;
  onUpdate: (patch: UpdateTaskDetailPayload) => void;
  onAttachTag: (tag: Tag) => void;
  onDetachTag: (tagId: string) => void;
}

export function TaskMetadataGrid({
  task,
  onUpdate,
  onAttachTag,
  onDetachTag,
}: TaskMetadataGridProps) {
  const { t } = useTranslation('tasks');
  const { toast } = useToast();
  const timeFormat = useUIStore((s) => s.timeFormat);
  const dateFormat = useUIStore((s) => s.dateFormat);
  const [expanded, setExpanded] = useState(false);

  const { data: fieldsResult, isLoading: fieldsLoading } = useProjectFields(task.projectId ?? '');
  const customFields = fieldsResult?.items ?? [];

  async function copyId() {
    await navigator.clipboard.writeText(task.id);
    toast.success({ title: t('dialog.idCopiedToast') });
  }

  return (
    <div data-testid="tasks-metadata-grid">
      <div className="flex items-center gap-2 py-2">
        <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={copyId}
              aria-label={t('dialog.idCopyTooltip')}
              className="group rounded-md border px-2 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:bg-secondary hover:text-secondary-foreground"
            >
              <span className="group-hover:hidden">{task.id}</span>
              <span className="hidden group-hover:inline">{t('dialog.idCopyTooltip')}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>{t('dialog.idCopyTooltip')}</TooltipContent>
        </Tooltip>

        {task.tags.length > 0 && <Separator orientation="vertical" className="h-4" />}

        <TagPicker
          projectId={task.projectId ?? ''}
          selectedTags={task.tags}
          onChange={(tags) => {
            const currentIds = new Set(task.tags.map((t) => t.id));
            const newTags = tags.filter((t) => !currentIds.has(t.id));
            const removedIds = new Set(tags.map((t) => t.id));
            newTags.forEach((tag) => onAttachTag(tag));
            task.tags.forEach((t) => {
              if (t.id && !removedIds.has(t.id)) {
                onDetachTag(t.id);
              }
            });
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-2 py-3 md:grid-cols-2">
        <MetadataRow icon={Flag} label={t('metadata.priority')}>
          <PriorityPicker priority={task.priority} onSelect={(p) => onUpdate({ priority: p })} />
        </MetadataRow>

        <MetadataRow icon={CircleDot} label={t('metadata.status')}>
          <StatusPicker
            boardId={task.status.boardId}
            currentStatus={task.status}
            onSelect={(statusId) => onUpdate({ statusId })}
          />
        </MetadataRow>

        <MetadataRow icon={Calendar} label={t('metadata.dueDate')}>
          <TaskDatePicker
            startDate={task.startDate}
            endDate={task.dueDate}
            statusType={task.status.type}
            dateFormat={dateFormat}
            timeFormat={timeFormat}
            onSave={(start, end) => onUpdate({ startDate: start, dueDate: end })}
          />
        </MetadataRow>

        <MetadataRow icon={User} label={t('metadata.assignee')}>
          <AssigneePicker
            projectId={task.projectId ?? ''}
            assignee={task.assignee}
            onSelect={(assigneeId) => onUpdate({ assigneeId })}
          />
        </MetadataRow>

        <MetadataRow icon={Clock} label={t('metadata.estimated')}>
          <EstimatedPicker
            value={task.estimatedMinutes}
            startDate={task.startDate}
            dueDate={task.dueDate}
            onSave={(v) => onUpdate({ estimatedMinutes: v })}
          />
        </MetadataRow>

        <MetadataRow icon={Timer} label={t('metadata.timer')}>
          <TaskTimerControl
            taskId={task.id}
            assigneeId={task.assignee?.id}
            totalTrackedHours={task.totalTrackedHours}
          />
        </MetadataRow>
      </div>

      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
        >
          <ChevronDown className="size-3.5" />
          {t('metadata.showMore')}
        </button>
      )}

      {expanded && (
        <>
          <div
            className="grid grid-cols-1 gap-x-6 gap-y-2 pb-2 md:grid-cols-2"
            data-testid="tasks-metadata-custom-fields"
          >
            <MetadataRow icon={Layers} label={t('metadata.group')}>
              <GroupPicker
                boardId={task.status.boardId}
                projectId={task.projectId ?? ''}
                currentGroupId={task.groupId}
                onSelect={(groupId) => onUpdate({ groupId })}
              />
            </MetadataRow>

            {fieldsLoading &&
              Array.from({ length: 2 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="h-5 w-full rounded"
                  data-testid="tasks-metadata-custom-fields-loading"
                />
              ))}

            {!fieldsLoading &&
              customFields.map((field) => (
                <FieldValueCell
                  key={field.id}
                  fieldId={field.id ?? ''}
                  taskId={task.id}
                  field={field}
                  mode="dialog"
                />
              ))}
          </div>

          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            <ChevronUp className="size-3.5" />
            {t('metadata.showLess')}
          </button>
        </>
      )}
    </div>
  );
}
